import { ErrorState, LoadingState } from '@playforge/ui';
import {
  BlockStack,
  Button,
  Card,
  Form,
  FormLayout,
  InlineStack,
  Page,
  Text,
  TextField,
  Toast,
} from '@shopify/polaris';
import { useState } from 'react';
import { apiClient, ApiError } from '../lib/api-client';
import { useApi } from '../lib/use-api';
import type { MerchantProfile, UpdateMerchantProfile } from '../lib/types';

const BRAND_COLOR_RE = /^#[0-9a-fA-F]{6}$/;

function SettingsForm({
  profile,
  onSaved,
}: {
  profile: MerchantProfile;
  onSaved: () => void;
}): JSX.Element {
  const [storeName, setStoreName] = useState(profile.storeName);
  const [email, setEmail] = useState(profile.email ?? '');
  const [country, setCountry] = useState(profile.country ?? '');
  const [currency, setCurrency] = useState(profile.currency ?? '');
  const [timezone, setTimezone] = useState(profile.timezone ?? '');
  const [language, setLanguage] = useState(profile.language ?? '');
  const [brandColor, setBrandColor] = useState(profile.brandColor ?? '');

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [colorError, setColorError] = useState<string | undefined>(undefined);
  const [toast, setToast] = useState<string | null>(null);

  const handleSubmit = async (): Promise<void> => {
    if (brandColor && !BRAND_COLOR_RE.test(brandColor)) {
      setColorError('Use a hex color like #1a2b3c');
      return;
    }
    setColorError(undefined);
    setError(null);
    setSaving(true);

    const patch: UpdateMerchantProfile = { storeName };
    if (email) patch.email = email;
    if (country) patch.country = country;
    if (currency) patch.currency = currency;
    if (timezone) patch.timezone = timezone;
    if (language) patch.language = language;
    if (brandColor) patch.brandColor = brandColor;

    try {
      await apiClient.put<MerchantProfile>('/merchant/profile', patch);
      setToast('Settings saved');
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <BlockStack gap="400">
      {error ? <ErrorState message={error} /> : null}
      <Card>
        <Form onSubmit={() => void handleSubmit()}>
          <FormLayout>
            <Text as="h2" variant="headingMd">
              Store profile
            </Text>
            <TextField
              label="Store name"
              value={storeName}
              onChange={setStoreName}
              autoComplete="off"
              requiredIndicator
            />
            <FormLayout.Group>
              <TextField
                label="Contact email"
                type="email"
                value={email}
                onChange={setEmail}
                autoComplete="email"
              />
              <TextField
                label="Brand color"
                value={brandColor}
                onChange={setBrandColor}
                autoComplete="off"
                placeholder="#1a2b3c"
                {...(colorError ? { error: colorError } : {})}
              />
            </FormLayout.Group>
            <FormLayout.Group>
              <TextField
                label="Country"
                value={country}
                onChange={setCountry}
                autoComplete="country"
                maxLength={2}
                placeholder="US"
              />
              <TextField
                label="Currency"
                value={currency}
                onChange={setCurrency}
                autoComplete="off"
                maxLength={3}
                placeholder="USD"
              />
            </FormLayout.Group>
            <FormLayout.Group>
              <TextField
                label="Timezone"
                value={timezone}
                onChange={setTimezone}
                autoComplete="off"
                placeholder="America/New_York"
              />
              <TextField
                label="Language"
                value={language}
                onChange={setLanguage}
                autoComplete="off"
                placeholder="en"
              />
            </FormLayout.Group>
          </FormLayout>
        </Form>
      </Card>
      <InlineStack align="end">
        <Button variant="primary" loading={saving} onClick={() => void handleSubmit()}>
          Save
        </Button>
      </InlineStack>
      {toast ? <Toast content={toast} onDismiss={() => setToast(null)} /> : null}
    </BlockStack>
  );
}

export function SettingsPage(): JSX.Element {
  const { data, loading, error, refetch } = useApi(
    () => apiClient.get<MerchantProfile>('/merchant/profile'),
    [],
  );

  return (
    <Page title="Settings" subtitle="Manage your store profile and branding">
      {loading ? <LoadingState label="Loading settings" /> : null}
      {error ? <ErrorState message={error.message} onRetry={refetch} /> : null}
      {data ? <SettingsForm profile={data} onSaved={refetch} /> : null}
    </Page>
  );
}
